import { useQuery } from '@tanstack/react-query'
import { ActivityIndicator } from './Shared/ActivityIndicator'
import {
  Box,
  InputLabel,
  MenuItem,
  Typography,
  FormControl,
  createFilterOptions,
  Popper,
  Autocomplete,
  TextField,
} from '@mui/material'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { Controller } from 'react-hook-form'
import { omit } from 'lodash'
import { useCurrentStateStore } from 'stores/useCurrentStateStore'

const MAX_LOCATIONS = 200

interface SelectOrganizationProps {
  fullWidth?: boolean
  required?: boolean
  error?: boolean
  label?: string
  organizationIdKey: string
  name?: string
  defaultValue?: number | null
  placeholder?: string
  inputProps?: Record<string, any>
  idsToFilter?: number[]
  activityIndicatorMaxWidth?: string
}

export const SelectFromAllOrganizations = ({
  label,
  placeholder,
  required,
  organizationIdKey,
  name = organizationIdKey,
  error,
  defaultValue,
  inputProps,
  idsToFilter,
  activityIndicatorMaxWidth,
  ...otherProps
}: SelectOrganizationProps) => {
  const get = useTypedApiClient({
    path: '/admin/organizations',
    method: 'get',
  })
  const { currentStateId } = useCurrentStateStore()

  const {
    isPending,
    isError,
    data: organizations,
  } = useQuery({
    queryKey: ['/organizations'],
    queryFn: async () => {
      const { data } = await get('', {
        headers: currentStateId
          ? { 'Current-State-Id': currentStateId.toString() }
          : undefined,
      })
      return data
    },
    select: (data) =>
      data.sort((a, b) => {
        const nameA = a.name ?? ''
        const nameB = b.name ?? ''
        return nameA.localeCompare(nameB)
      }),
  })

  if (isPending) {
    return <ActivityIndicator maxWidth={activityIndicatorMaxWidth} />
  }

  if (isError) {
    return <Typography>Error</Typography>
  }

  return (
    <Box data-testid="SelectFromAllOrganizations">
      <FormControl>
        {label ? (
          <InputLabel id={`${name}-label`} htmlFor={name} required={required}>
            {label}
          </InputLabel>
        ) : null}
        <Controller
          name={name}
          defaultValue={defaultValue ?? null}
          rules={{ required: false }}
          render={({ field: { onChange, value, ref } }) => (
            <Autocomplete
              filterOptions={createFilterOptions({
                matchFrom: 'any',
                limit: MAX_LOCATIONS,
              })}
              renderInput={(params) => (
                <TextField
                  data-testid="SelectFromAllOrganizations"
                  placeholder={placeholder}
                  error={error}
                  {...params}
                  {...inputProps}
                  inputRef={ref}
                />
              )}
              id={name}
              disablePortal
              options={
                organizations.filter((org) => !idsToFilter?.includes(org.id)) ??
                []
              }
              getOptionLabel={(option) => option.name ?? ''}
              ListboxProps={{ sx: { maxHeight: '250px' } }}
              PopperComponent={(props) => (
                <Popper
                  {...props}
                  popperOptions={{
                    modifiers: [
                      { name: 'flip', options: { fallbackPlacements: [] } },
                    ],
                  }}
                  placement="bottom"
                />
              )}
              renderOption={(props, org) => (
                <MenuItem
                  key={`organization-option-${org.id}`}
                  value={String(org.id)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                  {...omit(props, 'className', 'key')}
                >
                  {org.name}
                </MenuItem>
              )}
              {...otherProps}
              onChange={(_event, newValue) => onChange(newValue?.id ?? null)}
              value={organizations?.find((org) => org.id === value) ?? null}
            />
          )}
        />
      </FormControl>
    </Box>
  )
}
